import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { ChevronLeft, ChevronRight, Calendar, Menu, X } from 'lucide-react';
import './RequestDemoPage.css';
import logoDemo from '../../images/logo_white.png';
import { supabase } from '../../../config/supabase';

const RequestDemoPage = ({ logo, scrollToSection }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [step, setStep] = useState('date');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    companyName: '',
    companyEmail: '',
    companySize: '',
    location: '',
    comments: '',
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentYear = new Date().getFullYear();

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    if (newDate >= today) {
      setCurrentDate(newDate);
    }
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateSelect = (day) => {
    const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (selected >= today) {
      setSelectedDate(selected);
      setSelectedTime(null);
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleProceedToDetails = () => {
    if (selectedDate && selectedTime) {
      setStep('details');
    }
  };

  const resetForm = () => {
    setStep('date');
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({
      fullName: '',
      phoneNumber: '',
      companyName: '',
      companyEmail: '',
      companySize: '',
      location: '',
      comments: '',
    });
  };

  const handleSchedule = async () => {
    if (
      !formData.fullName ||
      !formData.phoneNumber ||
      !formData.companyName ||
      !formData.companyEmail ||
      !formData.companySize ||
      !formData.location ||
      !selectedDate ||
      !selectedTime
    ) {
      alert('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        full_name: formData.fullName,
        phone_number: formData.phoneNumber,
        company_name: formData.companyName,
        company_email: formData.companyEmail,
        company_size: formData.companySize,
        location: formData.location,
        comments: formData.comments || 'N/A',
        selected_date: selectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        selected_time: selectedTime,
      };

      // Save to Supabase and send email in parallel
      const [, emailResult] = await Promise.allSettled([
        supabase.from('demo_requests').insert([{
          full_name:      formData.fullName,
          phone_number:   formData.phoneNumber,
          company_name:   formData.companyName,
          company_email:  formData.companyEmail,
          company_size:   formData.companySize,
          location:       formData.location,
          comments:       formData.comments || null,
          scheduled_date: selectedDate.toISOString().split('T')[0],
          scheduled_time: selectedTime,
        }]),
        emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
          templateParams,
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        ),
      ]);

      if (emailResult.status === 'rejected') {
        console.error('EmailJS error:', emailResult.reason);
      }

      alert('Call scheduled successfully! Check your email for confirmation.');
      resetForm();
    } catch (error) {
      console.error('Schedule error:', error);
      alert('Failed to schedule call. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
  ];

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  return (
    <div className="requestdemo-container">
      <nav className="requestdemo-navbar">
        <div className="requestdemo-nav-content">
          <div className="requestdemo-logo" onClick={() => window.location.href = '/'}>
            <img src={logoDemo} alt="TalentScope360 Logo" />
          </div>

          <button
            className="requestdemo-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="requestdemo-nav-links requestdemo-nav-desktop">
            <a href="/#features" className="requestdemo-nav-link">
              Features
            </a>
            <a href="/pricing" className="requestdemo-nav-link">
              Pricing
            </a>
            <a href="/documentation" className="requestdemo-nav-link">
              Documentation
            </a>
          </div>

          <div className="requestdemo-nav-buttons requestdemo-nav-desktop">
            <a href="/login" className="requestdemo-btn-login">Login</a>
            <a href="/sign-up" className="requestdemo-btn-getstarted">Get Started</a>
          </div>
        </div>

        <div className={`requestdemo-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a
            href="/#features"
            className="requestdemo-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            Features
          </a>
          <a
            href="/#faq"
            className="requestdemo-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            FAQ
          </a>
          <a
            href="/documentation"
            className="requestdemo-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            Documentation
          </a>
          <div className="requestdemo-mobile-menu-buttons">
            <a href="/login" className="requestdemo-btn-login">Login</a>
            <a href="/sign-up" className="requestdemo-btn-getstarted">Get Started</a>
          </div>
        </div>
      </nav>

      <div className="requestdemo-wrapper">
        <div className="requestdemo-header">
          <h1 className="requestdemo-title">Let's Schedule a Call</h1>
          <p className="requestdemo-subtitle">
            Book a time that works for you. Our team is ready to discuss how we can help your business succeed.
          </p>
        </div>

        {step === 'date' ? (
          <div className="requestdemo-grid">
            <div className="requestdemo-card">
              <div className="requestdemo-calendar-content">
                <div className="requestdemo-calendar-header">
                  <h2 className="requestdemo-card-title">{monthName}</h2>
                  <div className="requestdemo-nav-buttons">
                    <button
                      onClick={handlePrevMonth}
                      className="requestdemo-nav-btn"
                      aria-label="Previous month"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={handleNextMonth}
                      className="requestdemo-nav-btn"
                      aria-label="Next month"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="requestdemo-weekdays">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="requestdemo-weekday">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="requestdemo-days-grid">
                  {emptyDays.map((_, i) => (
                    <div key={`empty-${i}`} className="requestdemo-empty-day" />
                  ))}

                  {days.map((day) => {
                    const isSelected =
                      selectedDate &&
                      selectedDate.getDate() === day &&
                      selectedDate.getMonth() === currentDate.getMonth() &&
                      selectedDate.getFullYear() === currentDate.getFullYear();

                    const dateToCheck = new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      day
                    );

                    const isDisabled = dateToCheck < today;

                    const isToday =
                      today.getDate() === day &&
                      today.getMonth() === currentDate.getMonth() &&
                      today.getFullYear() === currentDate.getFullYear();

                    return (
                      <button
                        key={day}
                        onClick={() => handleDateSelect(day)}
                        disabled={isDisabled}
                        className={`requestdemo-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="requestdemo-card">
              <h2 className="requestdemo-card-title">Select Time</h2>
              {selectedDate ? (
                <>
                  <p className="requestdemo-selected-date">
                    {selectedDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>

                  <div className="requestdemo-time-grid">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        className={`requestdemo-time-slot ${selectedTime === time ? 'selected' : ''}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleProceedToDetails}
                    disabled={!selectedTime}
                    className="requestdemo-btn requestdemo-btn-primary"
                  >
                    Continue
                  </button>
                </>
              ) : (
                <div className="requestdemo-empty-state">
                  <Calendar size={48} />
                  <p>Select a date to view available times</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="requestdemo-card requestdemo-form-card">
            <div className="requestdemo-form-header">
              <h2 className="requestdemo-card-title requestdemo-form-title">Request Information</h2>
              <p className="requestdemo-form-summary">
                {selectedDate?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                at {selectedTime}
              </p>
            </div>

            <div className="requestdemo-form-fields">
              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Full Name *</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="requestdemo-input"
                />
              </div>

              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Phone Number *</label>
                <input
                  type="tel"
                  placeholder="+254-712-345-678"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="requestdemo-input"
                />
              </div>

              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Company Name *</label>
                <input
                  type="text"
                  placeholder="Your Company"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="requestdemo-input"
                />
              </div>

              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Company Email *</label>
                <input
                  type="email"
                  placeholder="hello@company.com"
                  value={formData.companyEmail}
                  onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
                  className="requestdemo-input"
                />
              </div>

              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Company Size *</label>
                <select
                  value={formData.companySize}
                  onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                  className="requestdemo-select"
                >
                  <option value="">Select company size...</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501-1000">501-1,000 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </div>

              <div className="requestdemo-form-group">
                <label className="requestdemo-label">Location *</label>
                <input
                  type="text"
                  placeholder="City, Country"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="requestdemo-input"
                />
              </div>

              <div className="requestdemo-form-group requestdemo-form-group-full">
                <label className="requestdemo-label">Comments</label>
                <textarea
                  placeholder="Tell us more about your needs..."
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  className="requestdemo-textarea"
                  rows="4"
                />
              </div>
            </div>

            <div className="requestdemo-form-actions">
              <button
                onClick={() => setStep('date')}
                className="requestdemo-btn requestdemo-btn-secondary"
                disabled={isSubmitting}
              >
                Back
              </button>

              <button
                onClick={handleSchedule}
                disabled={
                  isSubmitting ||
                  !formData.fullName ||
                  !formData.phoneNumber ||
                  !formData.companyName ||
                  !formData.companyEmail ||
                  !formData.companySize ||
                  !formData.location
                }
                className="requestdemo-btn requestdemo-btn-primary"
              >
                {isSubmitting ? 'Sending...' : 'Schedule Call'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="requestdemo-footer-bottom">
        <p>© {currentYear} TalentScope360 | All rights reserved.</p>
      </div>
    </div>
  );
};

export default RequestDemoPage;